import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@heroui/react";
import MarketTable from "./MarketTable";
import MarketForm from "./MarketForm";
import { Spinner } from "@heroui/react";

const MatchResults = () => {
  const [markets, setMarkets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", status: 1 });

  const {
    isOpen: isMessageOpen,
    onOpen: onMessageOpen,
    onOpenChange: onMessageOpenChange,
    onClose: onMessageClose,
  } = useDisclosure();
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("alert");
  const [modalCallback, setModalCallback] = useState(null);
  const [modalAutoCloseMs, setModalAutoCloseMs] = useState(0);

  useEffect(() => {
    if (isMessageOpen && modalType === "alert" && modalAutoCloseMs > 0) {
      const timeoutId = setTimeout(() => {
        if (typeof modalCallback === "function") {
          const maybeFn = modalCallback();
          if (typeof maybeFn === "function") {
            maybeFn();
          }
        }
        if (typeof onMessageClose === "function") {
          onMessageClose();
        }
        setModalAutoCloseMs(0);
      }, modalAutoCloseMs);
      return () => clearTimeout(timeoutId);
    }
  }, [
    isMessageOpen,
    modalType,
    modalAutoCloseMs,
    modalCallback,
    onMessageClose,
  ]);

  const getAdminDashboardMarkets = async () => {
  try {
    const token = localStorage.getItem("jwt");
    if (!token) return console.warn("No token found");

    const response = await fetch(`${process.env.REACT_APP_API_HOST}/markets`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch markets");
    const result = await response.json();

    if (result?.data) {
      // 🧩 Convert Buffer fields
      const formattedData = result.data.map((item) => ({
        ...item,
        status: item.status?.data?.[0] === 1 ? 1 : 0,
        deleted: item.deleted?.data?.[0] === 1 ? 1 : 0,
      }));

      setMarkets(formattedData);
    }
  } catch (err) {
    console.error("Error fetching markets:", err);
  }
};


  useEffect(() => {
    getAdminDashboardMarkets();
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const isEditing = !!formData.id;
    const updatedMarkets = isEditing
      ? markets.map((m) => (m.id === formData.id ? formData : m))
      : [...markets, { ...formData, id: markets.length + 1 }];

    setMarkets(updatedMarkets);
    setShowForm(false);
    setFormData({ name: "", status: "active" });
  };

  const handleEdit = (market) => {
    // setFormData(market);

    setShowForm(true);
    setTimeout(() => {
      const mainContainer = document.querySelector("main");
      if (mainContainer) {
        mainContainer.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 0);
    const statusValue = market.status?.data?.[0] === 1 ? 1 : 0;

    setFormData({
      id: market.id,
      market_id: market.id,
      name: market.name || "",
      status: statusValue,
    });
    setShowForm(true);
  };

  // const handleDelete = (id) => {
  //   setMarkets(markets.filter((m) => m.id !== id));
  // };

  const handleDelete = (id) => {
    setModalMessage("Are you sure you want to delete this market?");
    setModalType("confirm");
    setModalCallback(() => () => {
      let headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      const urlencoded = {
        user_id: localStorage.getItem("uid"),
        delete_market_id: id,
      };

      let requestOptions = {
        method: "POST",
        headers: headers,
        body: JSON.stringify(urlencoded),
      };

      fetch(process.env.REACT_APP_API_HOST + "/deletemarket", requestOptions)
        .then((response) => {
          if (response.status === 401) {
            console.log("Unauthorized access. Please log in again.");
          } else {
            return response.json();
          }
        })
        .then((result) => {
          if (result?.success === true) {
            setModalMessage("Market deleted successfully!");
            setModalType("alert");
            setModalCallback(() => () => {
              getAdminDashboardMarkets();
            });
            setModalAutoCloseMs(1000);
            onMessageOpen();
          } else {
            setModalMessage(result?.message || "Failed to delete market.");
            setModalType("alert");
            setModalCallback(null);
            onMessageOpen();
          }
        })
        .catch((err) => {
          console.log("Error deleting market:", err);
          setModalMessage("Error deleting market.");
          setModalType("alert");
          setModalCallback(null);
          onMessageOpen();
        });
    });
    onMessageOpen();
  };
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Show spinner for 1 second on mount
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner size="lg" variant="spinner" color="warning" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900">Markets</h2>
        <Button
          color="primary"
          className="bg-admin_black text-white"
          onClick={() => {
            setShowForm(!showForm);
            setFormData({ name: "", status: 1 });
          }}
        >
          {showForm ? "Cancel" : "Add Market"}
        </Button>
      </div>

      {showForm && (
        <MarketForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowForm(false)}
          setShowForm={setShowForm}
          getAdminDashboardMarkets={getAdminDashboardMarkets}
        />
      )}
      <MarketTable
        markets={markets}
        onEdit={handleEdit}
        onDelete={handleDelete}
        getAdminDashboardMarkets={getAdminDashboardMarkets}

      />
      <Modal
        isOpen={isMessageOpen}
        onOpenChange={onMessageOpenChange}
        classNames={{
          backdrop: "bg-black/35",
          wrapper: "flex items-center justify-center min-h-screen",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              {modalType === "confirm" && (
                <>
                  <ModalBody className="flex items-start justify-center min-h-[60px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                  <ModalFooter className="-mt-4">
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      onPress={() => {
                        if (typeof modalCallback === "function") {
                          const maybeFn = modalCallback();
                          if (typeof maybeFn === "function") {
                            maybeFn();
                          }
                        }
                        onClose();
                      }}
                    >
                      Yes
                    </Button>
                  </ModalFooter>
                </>
              )}

              {modalType === "alert" && (
                <>
                  <ModalBody className="flex items-center justify-center min-h-[80px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};
export default MatchResults;
